import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useResolvedImageUrls } from "@/hooks/useResolvedImageUrls";
import { visiblePromotion, type HomepagePromotion } from "@/lib/homepage-promotion";

export function PromotionalBanner({ promotion, preview = false }: { promotion: HomepagePromotion | null; preview?: boolean }) {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      const current = Date.now();
      setNow(current);
      const next = Math.min(...[promotion?.startsAt, promotion?.endsAt].filter((v): v is string => !!v).map(Date.parse).filter(v => v > current));
      if (Number.isFinite(next)) timer = setTimeout(refresh, Math.min(next - current + 20, 2_147_483_647));
    };
    refresh();
    return () => clearTimeout(timer);
  }, [promotion]);
  const shown = preview ? promotion : now === null ? null : visiblePromotion(promotion, now);
  const images = useResolvedImageUrls(shown?.images ?? []);
  if (!shown?.heading || !shown.description || !shown.buttonText || !shown.destination) return null;
  return (
    <section aria-label="Homepage promotion" className="border-y border-primary/25 bg-secondary px-6 py-3 text-primary">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 md:flex-row">
        <div className="min-w-0 flex-1 text-center md:text-left">
          <h2 className="break-words font-display text-xl font-bold leading-tight md:text-2xl">{shown.heading}</h2>
          <p className="mt-1 break-words text-sm leading-relaxed">{shown.description}</p>
        </div>
        <div className="flex items-center gap-3 md:contents">
          {images.length > 0 && <div className="flex shrink-0 gap-2">{images.map((url, i) => <div key={i} className="h-14 w-14 overflow-hidden sm:h-16 sm:w-16">{url && <img src={url} alt={`Promotional image ${i + 1}`} className="h-full w-full object-contain" />}</div>)}</div>}
          <Button asChild className="h-auto min-h-10 max-w-full shrink-0 whitespace-normal break-words text-center"><a href={shown.destination}>{shown.buttonText}</a></Button>
        </div>
      </div>
    </section>
  );
}